import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Upload, Download, Edit, Trash2, Eye, Users, ArrowRightLeft, ChevronLeft, ChevronRight, AlertTriangle, FileText, FileSpreadsheet } from 'lucide-react';

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function FamiliesPage() {
  const { api, isAdmin, isSuperAdmin, isMasool, isMusaid, user, canExportExcel } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [families, setFamilies] = useState([]);
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300); // Debounce search by 300ms for mobile
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [areas, setAreas] = useState([]);
  const [showNonTagged, setShowNonTagged] = useState(searchParams.get('non_tagged') === 'true');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [editFamily, setEditFamily] = useState(null);
  const [viewFamily, setViewFamily] = useState(null);
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportScope, setExportScope] = useState('filtered'); // filtered, all
  const [exportOptions, setExportOptions] = useState({
    includeMemberCount: true,
    includeThaaliInfo: true,
    includeFMBStatus: true,
    includeContactInfo: true,
  });
  const [exporting, setExporting] = useState(false);

  const limit = 20;

  // Fetch families when filters change
  useEffect(() => {
    fetchFamilies();
  }, [page, search, selectedArea, selectedMohalla, selectedSector, showNonTagged]);

  // Fetch mohallas once on mount + areas
  useEffect(() => {
    fetchMohallas();
    api.get('/areas').then(r => setAreas(r.data.areas || [])).catch(() => {});
  }, []);

  // Fetch mohallas (filtered by area) and sectors when area/mohalla changes
  useEffect(() => {
    fetchMohallas();
  }, [selectedArea]);

  // Fetch sectors when mohalla changes
  useEffect(() => {
    fetchSectors();
  }, [selectedArea, selectedMohalla]);

  const fetchFamilies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (selectedArea) params.append('area', selectedArea);
      if (selectedMohalla) params.append('mohalla', selectedMohalla);
      if (selectedSector) params.append('sector', selectedSector);
      if (showNonTagged) params.append('non_tagged', 'true');
      
      const response = await api.get(`/families?${params}`);
      setFamilies(response.data.families);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch families');
    } finally {
      setLoading(false);
    }
  };

  const fetchMohallas = async () => {
    try {
      const params = selectedArea ? `?area=${encodeURIComponent(selectedArea)}` : '';
      const response = await api.get(`/mohallas${params}`);
      setMohallas(response.data.mohallas);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async () => {
    try {
      const qs = [];
      if (selectedArea) qs.push(`area=${encodeURIComponent(selectedArea)}`);
      if (selectedMohalla) qs.push(`mohalla=${encodeURIComponent(selectedMohalla)}`);
      const params = qs.length ? `?${qs.join('&')}` : '';
      const response = await api.get(`/sectors${params}`);
      setSectors(response.data.sectors);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  };

  const handleAddFamily = async (data) => {
    try {
      await api.post('/families', data);
      toast.success('Family added successfully');
      setIsAddOpen(false);
      fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add family');
    }
  };

  const handleUpdateFamily = async (hofId, data) => {
    try {
      await api.put(`/families/${hofId}`, data);
      toast.success('Family updated successfully');
      setEditFamily(null);
      fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update family');
    }
  };

  const handleDeleteFamily = async (hofId) => {
    if (!window.confirm('Are you sure you want to delete this family? All members will be removed.')) return;
    try {
      await api.delete(`/families/${hofId}`);
      toast.success('Family deleted successfully');
      fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete family');
    }
  };

  const handleBulkTransfer = async (targetMohalla, targetSector) => {
    try {
      await api.post('/bulk/transfer', {
        hof_ids: selectedFamilies,
        target_mohalla: targetMohalla,
        target_sector: targetSector
      });
      toast.success(`${selectedFamilies.length} families transferred successfully`);
      setSelectedFamilies([]);
      setIsTransferOpen(false);
      fetchFamilies();
    } catch (error) {
      toast.error('Failed to transfer families');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      
      // Scope handling
      if (exportScope === 'filtered') {
        if (selectedMohalla) params.append('mohalla', selectedMohalla);
        if (selectedSector) params.append('sector', selectedSector);
      }
      
      // Options
      params.append('format', exportFormat);
      params.append('include_member_count', exportOptions.includeMemberCount);
      params.append('include_thaali_info', exportOptions.includeThaaliInfo);
      params.append('include_fmb_status', exportOptions.includeFMBStatus);
      params.append('include_contact_info', exportOptions.includeContactInfo);
      
      const response = await api.get(`/download/families?${params}`, { responseType: 'blob' });
      const mimeType = exportFormat === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Families.${exportFormat === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${exportFormat.toUpperCase()} exported successfully`);
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // PDF export for Masool/Musaid - sector-wise only
  const handleSectorPdfExport = async () => {
    try {
      const mohalla = user?.mohalla;
      const sector = user?.sector;
      
      if (!mohalla) {
        toast.error('No mohalla assigned');
        return;
      }
      
      let url = `/export/sector-list-pdf?format=pdf`;
      if (sector) {
        // Musaid - export single sector
        url += `&scope=sector&mohalla=${encodeURIComponent(mohalla)}&sector=${encodeURIComponent(sector)}`;
      } else {
        // Masool - export all sectors in mohalla
        url += `&scope=mohalla&mohalla=${encodeURIComponent(mohalla)}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${sector || mohalla}_Sector_List.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const toggleSelectFamily = (hofId) => {
    setSelectedFamilies(prev => 
      prev.includes(hofId) ? prev.filter(id => id !== hofId) : [...prev, hofId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFamilies.length === families.length) {
      setSelectedFamilies([]);
    } else {
      setSelectedFamilies(families.map(f => f.id));
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="families-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">
            {showNonTagged ? 'Non-Tagged Families' : 'Families'}
          </h1>
          <p className="text-sm text-slate-500">
            {showNonTagged ? 'Families without sector assignment - assign them to a sector' : 'Manage HOF and family records'}
          </p>
        </div>
        
        {/* Export and Admin buttons */}
        <div className="flex items-center gap-2">
          {/* PDF Export for Masool/Musaid */}
          {(isMasool || isMusaid) && (
            <Button variant="outline" onClick={handleSectorPdfExport} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          )}
          
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setShowExportDialog(true)} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <UploadDialog onSuccess={fetchFamilies} api={api} />
              <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-navy hover:bg-navy-light" data-testid="add-family-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Family
                  </Button>
                </DialogTrigger>
                <FamilyFormDialog 
                  mohallas={mohallas} 
                  sectors={sectors}
                  onSubmit={handleAddFamily} 
                  onClose={() => setIsAddOpen(false)}
                />
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Non-Tagged Alert */}
      {showNonTagged && (
        <Card className="border-0 shadow-card border-l-4 border-l-orange-500 bg-orange-50/50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <div>
                <p className="font-medium text-orange-800">Non-Tagged Families</p>
                <p className="text-sm text-orange-600">These {total} families do not have a sector assigned. Select and transfer them to appropriate sectors.</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => { setShowNonTagged(false); setSearchParams({}); }}
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              Show All
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, mobile, HOF ID..."
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedArea || 'all'} onValueChange={(v) => { setSelectedArea(v === 'all' ? '' : v); setSelectedMohalla(''); setSelectedSector(''); setPage(1); }}>
              <SelectTrigger data-testid="area-filter">
                <SelectValue placeholder="Filter by Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {areas.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMohalla} onValueChange={(v) => { setSelectedMohalla(v === 'all' ? '' : v); setSelectedSector(''); setPage(1); }}>
              <SelectTrigger data-testid="mohalla-filter">
                <SelectValue placeholder="Filter by Mohalla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedSector} onValueChange={(v) => { setSelectedSector(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="sector-filter">
                <SelectValue placeholder="Filter by Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(s => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedFamilies.length > 0 && isAdmin && (
              <Dialog open={isTransferOpen} onOpenChange={setIsTransferOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-gold text-gold hover:bg-gold/10" data-testid="transfer-btn">
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transfer ({selectedFamilies.length})
                  </Button>
                </DialogTrigger>
                <TransferDialog 
                  mohallas={mohallas} 
                  sectors={sectors} 
                  count={selectedFamilies.length}
                  onSubmit={handleBulkTransfer}
                  onClose={() => setIsTransferOpen(false)}
                />
              </Dialog>
            )}
            {!showNonTagged && (
              <Button 
                variant="outline" 
                onClick={() => { setShowNonTagged(true); setSearchParams({ non_tagged: 'true' }); setPage(1); }}
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="show-non-tagged-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Non-Tagged
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table min-w-[800px] md:min-w-full">
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedFamilies.length === families.length && families.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                )}
                <TableHead className="whitespace-nowrap">HOF ITS</TableHead>
                <TableHead className="whitespace-nowrap min-w-[150px]">HOF Name</TableHead>
                <TableHead className="whitespace-nowrap">Members</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">Mobile</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">Area</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">Mohalla</TableHead>
                <TableHead className="whitespace-nowrap">Sector</TableHead>
                <TableHead className="hidden sm:table-cell whitespace-nowrap">Thaali</TableHead>
                <TableHead className="whitespace-nowrap">FMB</TableHead>
                <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 10} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : families.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 10} className="text-center py-8 text-slate-500">
                    No families found
                  </TableCell>
                </TableRow>
              ) : (
                families.map((family) => (
                  <TableRow key={family.id} data-testid={`family-row-${family.id}`}>
                    {isAdmin && (
                      <TableCell>
                        <Checkbox 
                          checked={selectedFamilies.includes(family.id)}
                          onCheckedChange={() => toggleSelectFamily(family.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-sm font-semibold text-emerald-700">{family.hof_its_id || '-'}</TableCell>
                    <TableCell className="font-medium text-sm">{family.hof_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-50">
                        <Users className="w-3 h-3 mr-1" />
                        {family.member_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{family.mobile}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{family.area || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{family.mohalla || '-'}</TableCell>
                    <TableCell className="text-sm">{family.sector || '-'}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{family.thaali_number || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${family.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}`}>
                        {family.fmb_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setViewFamily(family)} data-testid={`view-family-${family.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditFamily(family)} data-testid={`edit-family-${family.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteFamily(family.id)} data-testid={`delete-family-${family.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-2">
          <p className="text-xs sm:text-sm text-slate-500 text-center sm:text-left">
            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm text-slate-600">{page}/{totalPages || 1}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Edit Dialog */}
      {editFamily && (
        <Dialog open={!!editFamily} onOpenChange={() => setEditFamily(null)}>
          <FamilyFormDialog 
            family={editFamily}
            mohallas={mohallas} 
            sectors={sectors}
            onSubmit={(data) => handleUpdateFamily(editFamily.id, data)} 
            onClose={() => setEditFamily(null)}
          />
        </Dialog>
      )}

      {/* View Dialog */}
      {viewFamily && (
        <ViewFamilyDialog family={viewFamily} api={api} onClose={() => setViewFamily(null)} />
      )}

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Families
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5">
            {/* Export Scope */}
            <div>
              <Label className="text-sm font-medium">Export Scope</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div 
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    exportScope === 'filtered' ? 'border-navy bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setExportScope('filtered')}
                >
                  <p className="font-medium text-sm">Current Filter</p>
                  <p className="text-xs text-slate-500">Export {total} families based on current filters</p>
                </div>
                <div 
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    exportScope === 'all' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setExportScope('all')}
                >
                  <p className="font-medium text-sm">All Families</p>
                  <p className="text-xs text-slate-500">Export all families in the system</p>
                </div>
              </div>
            </div>

            {/* Export Format - Excel only for Super Admin */}
            <div>
              <Label className="text-sm font-medium">Export Format</Label>
              <div className={`grid gap-3 mt-2 ${canExportExcel ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div 
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                    exportFormat === 'pdf' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setExportFormat('pdf')}
                >
                  <FileText className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-sm">PDF</p>
                    <p className="text-xs text-slate-500">Print-ready format</p>
                  </div>
                </div>
                {canExportExcel && (
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                      exportFormat === 'excel' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setExportFormat('excel')}
                  >
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-sm">Excel</p>
                      <p className="text-xs text-slate-500">Editable spreadsheet</p>
                    </div>
                  </div>
                )}
              </div>
              {!canExportExcel && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mt-2">
                  📋 Excel export is restricted to Super Admin only
                </p>
              )}
            </div>

            {/* Export Options */}
            <div>
              <Label className="text-sm font-medium">Include in Export</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">Member Count</span>
                  <Checkbox 
                    checked={exportOptions.includeMemberCount}
                    onCheckedChange={(checked) => setExportOptions({...exportOptions, includeMemberCount: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">Thaali Information</span>
                  <Checkbox 
                    checked={exportOptions.includeThaaliInfo}
                    onCheckedChange={(checked) => setExportOptions({...exportOptions, includeThaaliInfo: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">FMB Status</span>
                  <Checkbox 
                    checked={exportOptions.includeFMBStatus}
                    onCheckedChange={(checked) => setExportOptions({...exportOptions, includeFMBStatus: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">Contact Information</span>
                  <Checkbox 
                    checked={exportOptions.includeContactInfo}
                    onCheckedChange={(checked) => setExportOptions({...exportOptions, includeContactInfo: checked})}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">Export Summary</p>
              <div className="text-xs text-blue-700 mt-1">
                <p>Scope: {exportScope === 'filtered' ? `Filtered (${total} families)` : 'All Families'}</p>
                <p>Format: {exportFormat.toUpperCase()}</p>
                {selectedMohalla && exportScope === 'filtered' && <p>Mohalla: {selectedMohalla}</p>}
                {selectedSector && exportScope === 'filtered' && <p>Sector: {selectedSector}</p>}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FamilyFormDialog({ family, mohallas, sectors, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    hof_its_id: family?.hof_its_id || '',
    hof_name: family?.hof_name || '',
    mobile: family?.mobile || '',
    address: family?.address || '',
    building_tag: family?.building_tag || '',
    flat_number: family?.flat_number || '',
    mohalla: family?.mohalla || '',
    sector: family?.sector || '',
    thaali_number: family?.thaali_number || '',
    fmb_status: family?.fmb_status || 'Not Going'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{family ? 'Edit Family' : 'Add New Family'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ITS Number - Primary field highlighted */}
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Label className="form-label text-emerald-700 font-semibold">HOF ITS Number *</Label>
          <Input 
            value={formData.hof_its_id} 
            onChange={(e) => setFormData({...formData, hof_its_id: e.target.value})}
            placeholder="Enter 8-digit ITS Number"
            className="mt-1 border-emerald-300 focus:border-emerald-500 text-lg font-mono"
            required
            data-testid="hof-its-input"
          />
          <p className="text-xs text-emerald-600 mt-1">ITS Number is the primary identifier</p>
        </div>
        <div>
          <Label className="form-label">HOF Name *</Label>
          <Input 
            value={formData.hof_name} 
            onChange={(e) => setFormData({...formData, hof_name: e.target.value})}
            required
            data-testid="hof-name-input"
          />
        </div>
        <div>
          <Label className="form-label">Mobile *</Label>
          <Input 
            value={formData.mobile} 
            onChange={(e) => setFormData({...formData, mobile: e.target.value})}
            required
            data-testid="mobile-input"
          />
        </div>
        <div>
          <Label className="form-label">Address *</Label>
          <Input 
            value={formData.address} 
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            required
            data-testid="address-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Building Tag</Label>
            <Input 
              value={formData.building_tag} 
              onChange={(e) => setFormData({...formData, building_tag: e.target.value})}
              data-testid="building-tag-input"
            />
          </div>
          <div>
            <Label className="form-label">Flat Number</Label>
            <Input 
              value={formData.flat_number} 
              onChange={(e) => setFormData({...formData, flat_number: e.target.value})}
              placeholder="e.g., A-101"
              data-testid="flat-number-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Mohalla</Label>
            <Select value={formData.mohalla} onValueChange={(v) => setFormData({...formData, mohalla: v})}>
              <SelectTrigger data-testid="mohalla-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {mohallas.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Sector</Label>
            <Select value={formData.sector} onValueChange={(v) => setFormData({...formData, sector: v})}>
              <SelectTrigger data-testid="sector-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {sectors.map(s => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Thaali Number</Label>
            <Input 
              value={formData.thaali_number} 
              onChange={(e) => setFormData({...formData, thaali_number: e.target.value})}
              data-testid="thaali-input"
            />
          </div>
          <div>
            <Label className="form-label">FMB Status</Label>
            <Select value={formData.fmb_status} onValueChange={(v) => setFormData({...formData, fmb_status: v})}>
              <SelectTrigger data-testid="fmb-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Going">Going</SelectItem>
                <SelectItem value="Not Going">Not Going</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit" className="bg-navy hover:bg-navy-light" disabled={loading} data-testid="save-family-btn">
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function TransferDialog({ mohallas, sectors, count, onSubmit, onClose }) {
  const [targetMohalla, setTargetMohalla] = useState('');
  const [targetSector, setTargetSector] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!targetMohalla || !targetSector) {
      toast.error('Please select both mohalla and sector');
      return;
    }
    setLoading(true);
    await onSubmit(targetMohalla, targetSector);
    setLoading(false);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Transfer {count} Families</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="form-label">Target Mohalla</Label>
          <Select value={targetMohalla} onValueChange={setTargetMohalla}>
            <SelectTrigger data-testid="transfer-mohalla-select">
              <SelectValue placeholder="Select Mohalla" />
            </SelectTrigger>
            <SelectContent>
              {mohallas.map(m => (
                <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="form-label">Target Sector</Label>
          <Select value={targetSector} onValueChange={setTargetSector}>
            <SelectTrigger data-testid="transfer-sector-select">
              <SelectValue placeholder="Select Sector" />
            </SelectTrigger>
            <SelectContent>
              {sectors.filter(s => s.mohalla === targetMohalla || !targetMohalla).map(s => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} className="bg-gold hover:bg-gold-dark" disabled={loading} data-testid="confirm-transfer-btn">
            {loading ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  );
}

function ViewFamilyDialog({ family, api, onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembers();
  }, [family.id]);

  const fetchMembers = async () => {
    try {
      const response = await api.get(`/families/${family.id}`);
      setMembers(response.data.members);
    } catch (error) {
      console.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {family.hof_name} - Family Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto max-h-[70vh]">
          {/* HOF ITS highlighted at top */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-emerald-700 font-medium">HOF ITS Number</span>
              <span className="font-mono text-lg font-bold text-emerald-800">{family.hof_its_id || '-'}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-slate-500">HOF ID:</span> <span className="font-mono">{family.id}</span></div>
            <div><span className="text-slate-500">Mobile:</span> {family.mobile}</div>
            <div><span className="text-slate-500">Address:</span> {family.address}</div>
            <div><span className="text-slate-500">Building:</span> {family.building_tag || '-'}</div>
            <div><span className="text-slate-500">Mohalla:</span> {family.mohalla || '-'}</div>
            <div><span className="text-slate-500">Sector:</span> {family.sector || '-'}</div>
            <div><span className="text-slate-500">Thaali:</span> {family.thaali_number || '-'}</div>
            <div><span className="text-slate-500">FMB Status:</span> 
              <Badge className={`ml-2 ${family.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}`}>
                {family.fmb_status}
              </Badge>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Family Members ({members.length})</h4>
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="whitespace-nowrap min-w-[90px]">ITS</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[180px]">Name</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[80px]">Relation</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[50px]">Age</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[60px]">Gender</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[70px]">Misaq</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[80px]">Title</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[120px]">Organisation</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[100px]">Qualification</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-mono text-xs text-emerald-700">{member.its_id || '-'}</TableCell>
                        <TableCell className="font-medium text-xs">{member.full_name}</TableCell>
                        <TableCell className="text-xs">{member.relationship}</TableCell>
                        <TableCell className="text-xs">{member.age}</TableCell>
                        <TableCell className="text-xs">{member.gender}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${member.misaq_status === 'Done' ? 'badge-success' : 'badge-warning'}`}>
                            {member.misaq_status === 'Done' ? '✓' : '✗'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{member.title || '-'}</TableCell>
                        <TableCell className="text-xs">{member.organization || '-'}</TableCell>
                        <TableCell className="text-xs">{member.qualification || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ onSuccess, api }) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/jamaat', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.families_created} families`);
      setIsOpen(false);
      setFile(null);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="upload-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Jamaat Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="file-upload"
              data-testid="file-input"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{file ? file.name : 'Click to select Excel file'}</p>
              <p className="text-xs text-slate-400 mt-1">Required columns: HOF Name, Mobile, Address</p>
            </label>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpload} disabled={!file || loading} className="bg-navy" data-testid="upload-submit-btn">
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
